/**
 * FeatureTeamChips — the teams a feature spans, as colour dots beside the
 * feature name in `FeatureRow`.
 *
 * A feature has no team of its own; it groups deliverables that each belong
 * to one team. The dots show which teams currently have work grouped under
 * the feature (derived on the client by `teamsForFeature`). Renders nothing
 * when the list is empty — a feature nobody has grouped work under shows no
 * dots, mirroring `FeatureStatusBadge`. Beyond `max` the rest collapse into
 * one "+k" chip whose tooltip names them.
 *
 * The team name is a hover hint (`title`) rather than visible text: a feature
 * row is dense, and spelling out three team names pushed the name input out
 * of the row, which is the whole reason the dot carries the identity here.
 * Beside the dot sits the number of that team's deliverables grouped under
 * the feature, so the row shows not just which teams are involved but how
 * much of the feature sits with each. The gap *between* chips is deliberately
 * wider than the gap *inside* one: at equal spacing "dot 3 dot 1" reads as an
 * undifferentiated run and the eye cannot tell which count belongs to which
 * colour.
 *
 * The name and the count are both in the accessible tree as one visually-
 * hidden phrase ("Platform: 3 deliverables"), with the visible digit marked
 * `aria-hidden` so it is not announced twice. A `title` is therefore never
 * the only carrier of meaning.
 *
 * The team colour is data (a hex the user picked), so it is applied inline,
 * the way `DependencyChip` applies it, rather than through a semantic token.
 * A team with no colour still gets a dot, in the neutral slate the rest of
 * the app falls back to — the dot IS the chip, so omitting it would drop the
 * team from the row entirely.
 *
 * @example
 * ```tsx
 * <FeatureTeamChips teams={[{ id: 't1', name: 'Platform', color: '#636df1', deliverableCount: 3 }]} />
 * <FeatureTeamChips teams={[]} /> // renders nothing
 * ```
 */

import { Badge } from '@lean-agility/design-system/react';
import { cn } from '@lean-agility/design-system/react';
import type { FeatureTeamRef } from '@/types/api';

/** Neutral slate used across the app when a team has no colour of its own. */
const FALLBACK_TEAM_COLOR = '#64748b';

/**
 * The one phrase both the hover hint and the accessible name use, so they can
 * never disagree about what a dot means.
 */
function describe(team: FeatureTeamRef): string {
  return `${team.name}: ${team.deliverableCount} deliverable${team.deliverableCount === 1 ? '' : 's'}`;
}

export interface FeatureTeamChipsProps {
  /** Teams with at least one deliverable grouped under the feature, in display order. */
  teams: FeatureTeamRef[];
  /**
   * How many dots to show before collapsing the rest into "+k".
   *
   * Higher than it was when each team rendered its full name: a dot costs a
   * fixed ~14px, so the row can hold several before crowding the input.
   */
  max?: number;
  /** Additional CSS classes. */
  className?: string;
}

export function FeatureTeamChips({ teams, max = 6, className }: FeatureTeamChipsProps) {
  if (teams.length === 0) return null;

  const shown = teams.slice(0, max);
  const hidden = teams.slice(max);

  return (
    <ul aria-label="Teams" className={cn('flex shrink-0 items-center gap-2.5', className)}>
      {shown.map((team, i) => (
        <li key={team.id ?? `${team.name}-${i}`} className="flex">
          <span className="inline-flex items-center gap-0.5 whitespace-nowrap" title={describe(team)}>
            <span
              aria-hidden="true"
              className="size-2.5 shrink-0 rounded-full ring-1 ring-border-default/40"
              style={{ backgroundColor: team.color || FALLBACK_TEAM_COLOR }}
            />
            <span aria-hidden="true" className="text-[10px] leading-none font-medium text-text-muted tabular-nums">
              {team.deliverableCount}
            </span>
            <span className="sr-only">{describe(team)}</span>
          </span>
        </li>
      ))}
      {hidden.length > 0 && (
        <li className="flex">
          <Badge variant="default" className="whitespace-nowrap" title={hidden.map((t) => t.name).join(', ')}>
            +{hidden.length}
          </Badge>
        </li>
      )}
    </ul>
  );
}
